/**
 * The clock, and the form for time that was not clocked.
 *
 * The timer lives on the server, not in this component: a running segment is a
 * row with no end time, so the clock survives a refresh, a closed laptop and a
 * different device. All this does is tick a display and post start and stop.
 *
 * Beside the elapsed time it shows what stopping *now* would bill, already
 * rounded up to the quarter hour. That number is the one that matters, and
 * seeing it before you stop is what stops "I worked 1h01" from being a surprise
 * 1.25 afterwards.
 *
 * Every control is a `Field`: label above the box, width on the field rather
 * than on the control. Both forms here lay out as flex rows that wrap, which is
 * exactly where a bare `<label>` puts its text beside the box instead of above
 * it -- and only for some of the boxes.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import type { ConsultingClient, RateKind, RunningTimer } from '@/api/types'
import { Field } from '@/components/ui'
import {
  billableSoFar,
  elapsedLabel,
  formatHours,
  rateLabel,
  todayIso,
} from '@/lib/consulting'

interface TimerProps {
  clients: ConsultingClient[]
  timer: RunningTimer
  onStart: (
    clientId: number,
    note: string,
    rate: { rate_kind?: RateKind; rate?: number | null },
  ) => Promise<void>
  onStop: (note: string) => Promise<void>
  onRecord: (payload: {
    client_id: number
    started_at: string
    ended_at: string
    note: string | null
    rate_kind?: RateKind
    rate?: number | null
  }) => Promise<void>
}

/**
 * The rate a stretch of work will bill at, prefilled from the client.
 *
 * Always visible rather than hidden behind a "use a different rate" checkbox.
 * The rate is a property of the row being created, and you want to see what it
 * is going to be *before* you press Start -- afterwards, changing it means
 * changing the whole day, including stretches already recorded at the old one.
 *
 * Choosing a client refills the box. Leaving it empty means the client's
 * default, never "no rate": the server resolves that, because an hourly row
 * with no amount prints on the invoice as Joint Venture.
 */
function useRateFields(clients: ConsultingClient[], clientId: number | '') {
  const [kind, setKind] = useState<RateKind>('hourly')
  const [rate, setRate] = useState('')
  // Keyed on the last client rather than on the effect's dependencies: a
  // refetch of `clients` must not overwrite a rate that has been typed over.
  const lastClient = useRef<number | '' | null>(null)

  useEffect(() => {
    if (lastClient.current === clientId) return
    lastClient.current = clientId
    const client = clients.find((entry) => entry.id === clientId)
    if (!client) return
    setKind(client.default_rate_kind)
    setRate(client.effective_rate === null ? '' : String(client.effective_rate))
  }, [clientId, clients])

  const payload =
    kind === 'joint_venture'
      ? { rate_kind: 'joint_venture' as RateKind, rate: null }
      : rate === ''
        ? {} // the server fills in the client's default
        : { rate_kind: 'hourly' as RateKind, rate: Number(rate) }

  return { kind, setKind, rate, setRate, payload }
}

function RateFields({ fields }: { fields: ReturnType<typeof useRateFields> }) {
  return (
    <>
      <Field label="Bills at" className="w-40">
        <select
          className="field"
          value={fields.kind}
          onChange={(event) => fields.setKind(event.target.value as RateKind)}
        >
          <option value="hourly">An hourly rate</option>
          <option value="joint_venture">Joint venture</option>
        </select>
      </Field>
      {fields.kind === 'hourly' && (
        <Field label="Rate" className="w-28">
          <input
            type="number"
            min="0"
            step="0.01"
            className="field"
            placeholder="Default"
            value={fields.rate}
            onChange={(event) => fields.setRate(event.target.value)}
          />
        </Field>
      )}
    </>
  )
}

export function ConsultingTimer({ clients, timer, onStart, onStop, onRecord }: TimerProps) {
  const running = timer.segment
  const [clientId, setClientId] = useState<number | ''>('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const rateFields = useRateFields(clients, clientId)

  // One interval, only while something is running. A timer that ticks on an
  // idle screen is a re-render every second for no reason.
  useEffect(() => {
    if (!running) return
    const handle = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(handle)
  }, [running])

  // The note field carries whatever was typed before the clock started, so
  // stopping does not silently discard it.
  useEffect(() => {
    if (running) setNote(running.note ?? '')
  }, [running])

  async function start(event: FormEvent) {
    event.preventDefault()
    if (busy || clientId === '') return
    setBusy(true)
    try {
      await onStart(clientId, note.trim(), rateFields.payload)
      setNote('')
    } finally {
      setBusy(false)
    }
  }

  async function stop() {
    if (busy) return
    setBusy(true)
    try {
      await onStop(note.trim())
      setNote('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card p-4">
      {running ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span
              aria-hidden
              className="size-2.5 shrink-0 animate-pulse rounded-full bg-crimson"
            />
            <span className="text-lg font-bold text-navy">{timer.client_name}</span>
            <span className="font-mono text-2xl font-bold tabular-nums">
              {elapsedLabel(running.started_at, now)}
            </span>
            {timer.rate_kind === 'joint_venture' ? (
              <span className="pill bg-info-light text-info-text">Joint Venture</span>
            ) : (
              <span className="pill bg-navy-soft text-navy">
                {rateLabel('hourly', timer.rate)}
              </span>
            )}
            <span className="text-sm text-ink-3">
              bills {formatHours(billableSoFar(running.started_at, now))} if you stop now
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <Field label="Working on" className="min-w-48 flex-1">
              <input
                className="field"
                placeholder="What are you working on?"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
            <button
              type="button"
              className="btn btn-danger shrink-0"
              onClick={() => void stop()}
              disabled={busy}
            >
              {busy ? 'Stopping...' : 'Stop'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={start} className="flex flex-wrap items-end gap-2">
          <Field label="Client" className="w-full sm:w-56">
            <select
              className="field"
              value={clientId}
              onChange={(event) =>
                setClientId(event.target.value ? Number(event.target.value) : '')
              }
            >
              <option value="">Choose a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} &mdash;{' '}
                  {rateLabel(client.default_rate_kind, client.effective_rate)}
                </option>
              ))}
            </select>
          </Field>
          <RateFields fields={rateFields} />
          <Field label="Working on" className="min-w-48 flex-1">
            <input
              className="field"
              placeholder="What are you working on? (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className="btn btn-primary shrink-0"
            disabled={busy || clientId === ''}
          >
            {busy ? 'Starting...' : 'Start'}
          </button>
        </form>
      )}

      <div className="mt-3 border-t border-line pt-3">
        <button
          type="button"
          className="text-sm font-semibold text-navy hover:underline"
          onClick={() => setManualOpen((open) => !open)}
          aria-expanded={manualOpen}
        >
          {manualOpen ? 'Hide' : 'Enter a start and stop time instead'}
        </button>
        {manualOpen && (
          <ManualEntry
            clients={clients}
            onRecord={async (payload) => {
              await onRecord(payload)
              setManualOpen(false)
            }}
          />
        )}
      </div>
    </section>
  )
}

function ManualEntry({
  clients,
  onRecord,
}: {
  clients: ConsultingClient[]
  onRecord: TimerProps['onRecord']
}) {
  const [clientId, setClientId] = useState<number | ''>('')
  const [day, setDay] = useState(todayIso())
  const [from, setFrom] = useState('09:00')
  const [to, setTo] = useState('10:00')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rateFields = useRateFields(clients, clientId)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (clientId === '') {
      setError('Choose a client.')
      return
    }
    const started = new Date(`${day}T${from}`)
    const ended = new Date(`${day}T${to}`)
    if (!(ended > started)) {
      // Caught here as well as on the server so a typo does not cost a round
      // trip; the server checks it again because the API is drivable by script.
      setError('The stop time has to be after the start time.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      await onRecord({
        client_id: clientId,
        started_at: started.toISOString(),
        ended_at: ended.toISOString(),
        note: note.trim() || null,
        ...rateFields.payload,
      })
      setNote('')
    } catch (caught) {
      // The panel stays open and keeps what was typed. The server writes its
      // own messages ("The stop time has to be after the start time."), and
      // replacing them with a generic one throws away the useful half.
      setError(caught instanceof ApiError ? caught.message : 'Could not record that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Client" className="w-full sm:w-56">
          <select
            className="field"
            value={clientId}
            onChange={(event) => setClientId(event.target.value ? Number(event.target.value) : '')}
          >
            <option value="">Choose...</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date" className="w-40">
          <input
            type="date"
            className="field"
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
        </Field>
        <Field label="From" className="w-32">
          <input
            type="time"
            className="field"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </Field>
        <Field label="To" className="w-32">
          <input
            type="time"
            className="field"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </Field>
        <RateFields fields={rateFields} />
      </div>

      <Field label="Activity">
        <input
          className="field"
          placeholder="What did you work on?"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>

      {error && <p className="text-sm text-error-text">{error}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? 'Recording...' : 'Record'}
        </button>
        <span className="text-xs text-ink-3">
          The stop time is moved forward to the next quarter hour.
        </span>
      </div>
    </form>
  )
}
